<template>
	<a-modal class="g-header-ip-brand-edit-modal" :visible="visible" title="头部模块编辑" width="618px" :centered="true"
		:mask-closable="false" :destroy-on-close="true" @cancel="onCancel">
		<div class="g-header-ip-brand-edit" @click="clearPanel">
			<div class="g-header-ip-brand-edit__preview" :style="previewStyle" @click.stop>
				<UploadProgressPanel v-if="isUploading" :tasks="uploadTasks" variant="item" hide-filename />
				<template v-else>
					<img v-if="bgUrl" :src="bgUrl" class="g-header-ip-brand-edit__preview-bg" alt="" />
					<div class="g-header-ip-brand-edit__preview-content-wrapper">
						<div class="g-header-ip-brand-edit__preview-content">
							<div
								v-if="form.showBadge"
								contenteditable="true"
								class="g-header-ip-brand-edit__badge-input"
								:style="badgeStyle"
								@input="onBadgeInput"
								@keydown="onBadgeKeydown"
								@focus="onBadgeFocus"
								ref="badgeRefEl"
							></div>
							<textarea v-model="form.title" class="g-header-ip-brand-edit__title-input"
								:style="{ color: form.textColor }" maxlength="40" rows="2" placeholder="输入主标题" />
							<input v-model="form.subtitle" type="text" class="g-header-ip-brand-edit__subtitle-input"
								:style="{ color: form.textColor }" maxlength="60" placeholder="输入副标题" />
							<div v-if="form.showFeatures" class="g-header-ip-brand-edit__features">
								<div v-for="(item, index) in form.features" :key="item.id"
									class="g-header-ip-brand-edit__feature" :style="{ color: form.textColor }">
									<span v-if="index > 0" class="g-header-ip-brand-edit__divider"
										aria-hidden="true"></span>
									<span class="g-header-ip-brand-edit__feature-icon" :style="{ background: form.featureIconColor }">
										<i class="mbm-iconfont" :class="item.icon"></i>
									</span>
									<input v-model="item.label" type="text"
										class="g-header-ip-brand-edit__feature-input" maxlength="8" placeholder="文案" />
								</div>
							</div>
						</div>
					</div>

					<input v-if="form.showTag" v-model="form.tag" type="text" class="g-header-ip-brand-edit__tag-input"
						:style="tagStyle" maxlength="12" placeholder="角标" />
				</template>
			</div>

			<div class="g-header-ip-brand-edit__panel" @click.stop>
				<div class="g-header-ip-brand-edit__link-row">
					<i class="mbm-iconfont mbm-lianjie"></i>
					<span :class="{ 'is-empty': !jumpLinkLabel }">{{ jumpLinkLabel || '未设置链接' }}</span>
				</div>
				<div class="g-header-ip-brand-edit__toolbar">
					<button type="button" class="g-header-ip-brand-edit__action"
						@click="jumpLinkVisible = true">换链接</button>
					<button type="button" class="g-header-ip-brand-edit__action" @click="triggerBgSelect">换底图</button>
					<button type="button" class="g-header-ip-brand-edit__action"
						:class="{ 'is-active': !form.showBadge }" @click="form.showBadge = !form.showBadge">{{
							form.showBadge ? '隐藏标签' : '显示标签' }}</button>
					<button type="button" class="g-header-ip-brand-edit__action" :class="{ 'is-active': !form.showTag }"
						@click="form.showTag = !form.showTag">{{ form.showTag ? '隐藏角标' : '显示角标' }}</button>
					<button type="button" class="g-header-ip-brand-edit__action"
						:class="{ 'is-active': !form.showFeatures }" @click="form.showFeatures = !form.showFeatures">{{
							form.showFeatures ? '隐藏卖点' : '显示卖点' }}</button>
					<div class="g-header-ip-brand-edit__color-picker">
						<ColorPicker
							v-model="form.textColor"
							title="文字颜色"
							@change="val => form.textColor = val"
						/>
					</div>
					<div class="g-header-ip-brand-edit__color-picker">
						<ColorPicker
							v-if="form.showBadge || form.showTag"
							v-model="form.badgeTagBgColor"
							title="标签角标背景色"
							@change="val => form.badgeTagBgColor = val"
						/>
					</div>
					<div class="g-header-ip-brand-edit__color-picker">
						<ColorPicker
							v-if="form.showFeatures"
							v-model="form.featureIconColor"
							title="卖点图标背景色"
							@change="val => form.featureIconColor = val"
						/>
					</div>
				</div>
			</div>
		</div>

		<template #footer>
			<button type="button" class="g-header-ip-brand-edit__footer-btn" @click="onCancel">取消</button>
			<button type="button" class="g-header-ip-brand-edit__footer-btn is-confirm" :disabled="isUploading"
				@click="onConfirm">确定</button>
		</template>

		<input ref="bgInput" type="file" accept="image/*" class="g-header-ip-brand-edit__file-input"
			@change="onBgFileChange" />

		<JumpLink :visible="jumpLinkVisible" :jumpLink="form.jumpLink || emptyJumpLink" :linkTypes="['qrcode']"
			@submit="onJumpLinkSubmit" @cancel="jumpLinkVisible = false" />
	</a-modal>
</template>

<script>
import JumpLink from '@/components/jumpLink.vue';
import ColorPicker from './ColorPicker.vue';
import {
	filterValidLocalFiles,
	revokeLocalImageUrl,
} from '../utils/localImage.js';
import UploadProgressPanel from './UploadProgressPanel.vue';
import {
	createUploadTasks,
	updateUploadTaskProgress,
	uploadHeaderMediaFiles,
} from '../utils/uploadHeaderMedia.js';
import { createEmptyJumpLink, formatJumpLinkLabel } from '../utils/jumpLink.js';
import {
	getHeaderIpBrandDefaults,
	getHeaderIpBrandImageUrl,
	normalizeHeaderIpBrandData,
} from '../utils/headerIpBrand.js';

export default {
	name: 'GHeaderIpBrandEditModal',
	components: { JumpLink, UploadProgressPanel, ColorPicker },
	props: {
		visible: {
			type: Boolean,
			default: false,
		},
		value: {
			type: Object,
			default: () => ({}),
		},
	},
	emits: ['update:visible', 'confirm', 'cancel'],
	data() {
		return {
			form: getHeaderIpBrandDefaults(),
			activePanel: '',
			jumpLinkVisible: false,
			emptyJumpLink: createEmptyJumpLink(),
			isUploading: false,
			uploadTasks: [],
		};
	},
	computed: {
		previewStyle() {
			return {
				height: `${this.form.height}px`,
			};
		},
		bgUrl() {
			return getHeaderIpBrandImageUrl(this.form.image);
		},
		jumpLinkLabel() {
			return formatJumpLinkLabel(this.form.jumpLink);
		},
		badgeStyle() {
			const bgColor = this.form.badgeTagBgColor || '#325fb2';
			const textColor = this.form.textColor || '#ffffff';
			return {
				color: textColor,
				background: `linear-gradient(90deg, ${bgColor} 0%, ${bgColor}aa 100%)`,
			};
		},
		tagStyle() {
			const bgColor = this.form.badgeTagBgColor || '#325fb2';
			const textColor = this.form.textColor || '#ffffff';
			return {
				color: textColor,
				background: `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}aa 100%)`,
			};
		},
	},
	watch: {
		visible(val) {
			if (val) {
				this.activePanel = '';
				this.syncForm();
			}
		},
		value: {
			deep: true,
			handler() {
				if (this.visible) {
					this.syncForm();
				}
			},
		},
		'form.showBadge'(val) {
			if (val) {
				this.$nextTick(() => this.syncBadgeDom());
			}
		},
	},
	methods: {
		syncBadgeDom() {
			const el = this.$refs.badgeRefEl;
			if (!el) return;
			if (el.textContent !== this.form.badge) {
				el.textContent = this.form.badge || '';
			}
		},
		syncForm() {
			this.form = normalizeHeaderIpBrandData(this.value);
			this.$nextTick(() => this.syncBadgeDom());
		},
		onBadgeInput(e) {
			const text = e.target.textContent || '';
			if (text.length > 24) {
				e.target.textContent = text.slice(0, 24);
				this.placeCursorAtEnd(e.target);
			}
			this.form.badge = e.target.textContent || '';
		},
		onBadgeKeydown(e) {
			if (e.key === 'Enter') {
				e.preventDefault();
				e.target.blur();
			}
		},
		onBadgeFocus(e) {
			this.clearPanel();
		},
		placeCursorAtEnd(el) {
			const range = document.createRange();
			const sel = window.getSelection();
			range.selectNodeContents(el);
			range.collapse(false);
			sel.removeAllRanges();
			sel.addRange(range);
		},
		clearPanel() {
			this.activePanel = '';
		},
		togglePanel(panel) {
			this.activePanel = this.activePanel === panel ? '' : panel;
		},
		triggerBgSelect() {
			this.$refs.bgInput?.click();
		},
		async onBgFileChange(event) {
			const file = (event.target.files || [])[0];
			event.target.value = '';
			const validFiles = filterValidLocalFiles(file ? [file] : []);
			if (!validFiles.length) return;
			this.isUploading = true;
			this.uploadTasks = createUploadTasks([validFiles[0]]);
			try {
				const [item] = await uploadHeaderMediaFiles(this, {
					type: 'image',
					files: [validFiles[0]],
					onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
				});
				if (this.form.image?.local) {
					revokeLocalImageUrl(this.form.image.previewUrl);
				}
				this.form.image = item;
			} catch (error) {
				this.$message?.error?.('上传失败');
			} finally {
				this.isUploading = false;
				this.uploadTasks = [];
			}
		},
		onJumpLinkSubmit(jumpLink) {
			this.form.jumpLink = jumpLink?.type ? { ...jumpLink } : null;
			this.jumpLinkVisible = false;
		},
		onCancel() {
			this.$emit('update:visible', false);
			this.$emit('cancel');
		},
		onConfirm() {
			this.$emit('confirm', normalizeHeaderIpBrandData(this.form));
			this.$emit('update:visible', false);
		},
	},
};
</script>

<style scoped lang="less">
@import '../styles/edit-float-panel.less';
.g-header-ip-brand-edit__color-picker{
	margin-left: 8px;
	display: flex;
}

.g-header-ip-brand-edit {
	padding: 4px 0 12px;
}

.g-header-ip-brand-edit__preview {
	position: relative;
	overflow: hidden;
	margin-bottom: 16px;
	background: #163a8a;
}

.g-header-ip-brand-edit__preview-bg {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: right center;
}

.g-header-ip-brand-edit__preview-content {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	box-sizing: border-box;
}
.g-header-ip-brand-edit__preview-content-wrapper{
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
	display: flex;
	z-index: 2;
	align-items: center;
	padding-left: 28px;
}
.g-header-ip-brand-edit__badge-input {
	display: inline-block;
	width: auto;
	min-width: 40px;
	padding: 6px 12px;
	border: none;
	border-radius: 999px;
	background: linear-gradient(90deg, #325fb2 0%, #0c35aa 100%);
	outline: none;
	font-size: 13px;
	font-weight: 500;
	line-height: 1.3;
}

.g-header-ip-brand-edit__title-input {
	width: 240px;
	max-width: 100%;
	margin-top: 16px;
	border: none;
	background: transparent;
	outline: none;
	resize: none;
	font-size: 30px;
	font-weight: 700;
	line-height: 1.35;
	white-space: pre-line;
}

.g-header-ip-brand-edit__subtitle-input {
	width: 240px;
	max-width: 100%;
	margin-top: 10px;
	border: none;
	background: transparent;
	outline: none;
	font-size: 14px;
	font-weight: 400;
	line-height: 1.5;
}

.g-header-ip-brand-edit__features {
	display: flex;
	align-items: stretch;
	width: auto;
	max-width: 100%;
	margin-top: 28px;
	padding-top: 0;
}

.g-header-ip-brand-edit__feature {
	position: relative;
	display: flex;
	flex: 0 0 auto;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	width: 72px;
	padding: 0;
}

.g-header-ip-brand-edit__feature-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background: #3039b4;

	i {
		font-size: 14px;
		line-height: 1;
	}
}

.g-header-ip-brand-edit__feature-input {
	width: 100%;
	border: none;
	background: transparent;
	outline: none;
	font-size: 12px;
	font-weight: 500;
	line-height: 1.3;
	text-align: center;
	color: inherit;
}

.g-header-ip-brand-edit__divider {
	position: absolute;
	left: 0;
	top: 50%;
	width: 1px;
	height: 28px;
	transform: translateY(-50%);
	background: rgba(255, 255, 255, 0.28);
}

.g-header-ip-brand-edit__tag-input {
	position: absolute;
	right: 10%;
	top: 65%;
	z-index: 3;
	width: 88px;
	padding: 5px 10px;
	border: none;
	border-radius: 999px;
	background: linear-gradient(135deg, #2f58da 0%, #6e3fe4 100%);
	outline: none;
	font-size: 12px;
	font-weight: 600;
	line-height: 1.3;
	text-align: center;
	box-shadow: 0 6px 16px rgba(40, 60, 180, 0.28);
}

.g-header-ip-brand-edit__panel {
	width: max-content;
	max-width: 100%;
	border: 1px solid #eceef2;
	border-radius: 4px;
	overflow: visible;
}

.g-header-ip-brand-edit__link-row {
	display: flex;
	align-items: center;
	gap: 8px;
	min-height: 40px;
	padding: 0 10px;
	border-bottom: 1px solid #f0f0f0;
	font-size: 13px;
	color: #666;

	i {
		color: #999;
	}

	.is-empty {
		color: #bbb;
	}
}

.g-header-ip-brand-edit__toolbar {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0;
	min-height: 40px;
	padding: 0 10px;
	max-width: 100%;
}

.g-header-ip-brand-edit__action {
	border: none;
	background: transparent;
	padding: 0 8px;
	font-size: 13px;
	color: #285af9;
	cursor: pointer;
	line-height: 32px;
	white-space: nowrap;
	flex-shrink: 0;

	&:hover:not(:disabled) {
		color: #1d4ed8;
	}

	&.is-active {
		color: #1d4ed8;
		font-weight: 500;
	}
}

.g-header-ip-brand-edit__action-wrap {
	position: relative;
	display: inline-flex;
	align-items: center;
}

.g-header-ip-brand-edit__sub-panel {
	position: absolute;
	left: 0;
	top: calc(100% + 6px);
	z-index: 10;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 10px;
	background: #fff;
	border: 1px solid #eceef2;
	border-radius: 4px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
	white-space: nowrap;
}

.g-header-ip-brand-edit__sub-label {
	font-size: 12px;
	color: #666;
}

.g-header-ip-brand-edit__sub-input {
	width: 88px;
}

.g-header-ip-brand-edit__sub-confirm {
	flex-shrink: 0;
	height: 28px;
	padding: 0 12px;
	border: none;
	border-radius: 4px;
	background: #285af9;
	color: #fff;
	font-size: 12px;
	line-height: 28px;
	white-space: nowrap;
	cursor: pointer;
}

.g-header-ip-brand-edit__file-input {
	display: none;
}

.g-header-ip-brand-edit__footer-btn {
	min-width: 72px;
	height: 32px;
	padding: 0 16px;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	background: #fff;
	font-size: 14px;
	color: #666;
	cursor: pointer;

	&.is-confirm {
		border-color: #285af9;
		background: #285af9;
		color: #fff;
	}

	&+& {
		margin-left: 10px;
	}
}
</style>
